/**
 * Schritt-1-Eingaben vor dem ersten Speichern puffern.
 *
 * Ein Entwurf entsteht erst beim Übergang zu Schritt 2. Bis dahin gingen Anzeige, Ergänzungen, Name und Jobtitel bei
 * F5 oder Tab-Schließen verloren. Der Puffer liegt in `sessionStorage` (pro Browser-Tab) unter einem Schlüssel pro
 * Nutzer und wird gelöscht, sobald der Entwurf angelegt ist. Jeder Zugriff ist abgesichert: ohne Speicher passiert nichts.
 */

import { WizardState } from './campaign-wizard.types';

export type Step1Buffer = Pick<
  WizardState,
  'briefing' | 'briefingExtras' | 'name' | 'jobTitle' | 'accountId' | 'adCheck'
>;

const PREFIX = 'wizStep1Buffer:';
const MAX_TEXT = 70000;

const keyOf = (userId: string | undefined | null): string => `${PREFIX}${userId || 'anon'}`;

const text = (value: unknown): string => (typeof value === 'string' ? value.slice(0, MAX_TEXT) : '');

const hasContent = (b: Step1Buffer): boolean =>
  Boolean(b.briefing.trim() || b.briefingExtras.trim() || b.name.trim() || b.jobTitle.trim());

export const pickStep1 = (state: WizardState): Step1Buffer => ({
  briefing: state.briefing,
  briefingExtras: state.briefingExtras,
  name: state.name,
  jobTitle: state.jobTitle,
  accountId: state.accountId,
  adCheck: state.adCheck,
});

/** Schreibt den Puffer; ohne Inhalt wird er stattdessen gelöscht. */
export const writeStep1Buffer = (userId: string | undefined | null, state: WizardState): void => {
  try {
    const buffer = pickStep1(state);
    if (hasContent(buffer)) window.sessionStorage.setItem(keyOf(userId), JSON.stringify(buffer));
    else window.sessionStorage.removeItem(keyOf(userId));
  } catch {
    // Ohne Speicher gibt es keinen Puffer; der Wizard funktioniert wie zuvor.
  }
};

export const readStep1Buffer = (userId: string | undefined | null): Step1Buffer | null => {
  try {
    const raw = window.sessionStorage.getItem(keyOf(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const adCheck = parsed.adCheck;
    const buffer: Step1Buffer = {
      briefing: text(parsed.briefing),
      briefingExtras: text(parsed.briefingExtras),
      name: text(parsed.name).slice(0, 200),
      jobTitle: text(parsed.jobTitle).slice(0, 200),
      accountId: text(parsed.accountId).slice(0, 100),
      adCheck:
        adCheck && typeof adCheck === 'object' && Array.isArray((adCheck as { items?: unknown }).items)
          ? (adCheck as Step1Buffer['adCheck'])
          : null,
    };
    return hasContent(buffer) ? buffer : null;
  } catch {
    return null;
  }
};

export const clearStep1Buffer = (userId: string | undefined | null): void => {
  try {
    window.sessionStorage.removeItem(keyOf(userId));
  } catch {
    // nichts zu löschen
  }
};

/** Stellt den Puffer wieder her; nur leere Felder (Vorbelegung aus dem KI-Chat hat Vorrang). */
export const applyStep1Buffer = (state: WizardState, buffer: Step1Buffer | null): WizardState => {
  if (!buffer) return state;
  const briefingEmpty = !state.briefing.trim();
  return {
    ...state,
    briefing: briefingEmpty ? buffer.briefing : state.briefing,
    // Ergänzungen und Prüfergebnis gehören zur gepufferten Anzeige.
    briefingExtras: briefingEmpty && !state.briefingExtras.trim() ? buffer.briefingExtras : state.briefingExtras,
    adCheck: briefingEmpty ? buffer.adCheck : state.adCheck,
    name: state.name.trim() ? state.name : buffer.name,
    jobTitle: state.jobTitle.trim() ? state.jobTitle : buffer.jobTitle,
    accountId: state.accountId ? state.accountId : buffer.accountId,
  };
};
